/**
 * Legal Consent Service — lecturas y la llamada a la callable del consentimiento
 * (ADR-SEC-018). Sin estado: la orquestación vive en el store `legalConsent`.
 *
 * - Las dos lecturas van **contra el servidor** (`getDocFromServer`): deciden si
 *   se muestra un modal que bloquea la sesión, y una respuesta de caché vacía se
 *   leería como «el usuario no existe» o «no hay versión» sin ningún error
 *   (ADR-021, `specs/DISENO.md`).
 * - La escritura del consentimiento NO se hace aquí: la hace la Cloud Function
 *   `acceptLegalTerms`, único escritor (specs/API-CONTRACT/accept-legal-terms.md).
 */
import { doc, getDocFromServer } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from './firebase/config'
import { LEGAL_CLIENT_ID } from '@/constants/legal'
import type {
  AcceptLegalTermsResponse,
  LegalChannel,
  LegalTermsErrorCode,
} from '@/types/legal'

export interface CurrentLegalVersion {
  version_no: string
  description: string
}

export interface UserLegalState {
  exists: boolean
  acceptedVersion: string | null
  displayName: string | null
}

/** `legaldocs_current_version/current`, o `null` si no existe o no tiene versión usable. */
export async function fetchCurrentLegalVersion(): Promise<CurrentLegalVersion | null> {
  const snap = await getDocFromServer(doc(db, 'legaldocs_current_version', 'current'))
  if (!snap.exists()) return null
  const data = snap.data()
  const version = data.version_no
  if (typeof version !== 'string' || version.trim() === '') return null
  return {
    version_no: version,
    description: typeof data.description === 'string' ? data.description : '',
  }
}

/** Estado de consentimiento de `users/{uid}` leído del servidor. */
export async function fetchUserLegalState(uid: string): Promise<UserLegalState> {
  const snap = await getDocFromServer(doc(db, 'users', uid))
  if (!snap.exists()) return { exists: false, acceptedVersion: null, displayName: null }
  const data = snap.data()
  return {
    exists: true,
    acceptedVersion:
      typeof data.accepted_legal_version === 'string' ? data.accepted_legal_version : null,
    displayName: typeof data.display_name === 'string' ? data.display_name : null,
  }
}

/** Llama a `acceptLegalTerms` (us-central1). Lanza el `FunctionsError` tal cual. */
export async function callAcceptLegalTerms(
  acceptedVersion: string,
  channel: LegalChannel
): Promise<AcceptLegalTermsResponse> {
  const callable = httpsCallable<
    { accepted_version: string; channel: LegalChannel; client: typeof LEGAL_CLIENT_ID },
    AcceptLegalTermsResponse
  >(functions, 'acceptLegalTerms')
  const result = await callable({
    accepted_version: acceptedVersion,
    channel,
    client: LEGAL_CLIENT_ID,
  })
  return result.data
}

/** `details.code` de un error de la callable, si es uno de los que el cliente distingue. */
export function getLegalTermsErrorCode(error: unknown): LegalTermsErrorCode | null {
  if (typeof error !== 'object' || error === null || !('details' in error)) return null
  const details = (error as { details: unknown }).details
  if (typeof details !== 'object' || details === null) return null
  const code = (details as { code?: unknown }).code
  return code === 'LEGAL_VERSION_MISSING' ||
    code === 'TERMS_VERSION_OUTDATED' ||
    code === 'USER_NOT_PROVISIONED'
    ? code
    : null
}
